"use client"

import type React from "react"

import { useId, useState } from "react"
import { Form } from "react-bootstrap"
import { Eye, EyeOff } from "lucide-react"
import { useTranslation } from "../../hooks/use-translation"

interface PasswordInputProps {
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>

  // Standard input passthroughs
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  autoComplete?: string

  // Custom styling
  className?: string
  style?: React.CSSProperties
}

export function PasswordInput({
  value,
  onChange,
  id,
  name,
  placeholder,
  required,
  disabled,
  autoComplete,
  className = "",
  style,
}: PasswordInputProps) {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const generatedId = useId()
  const inputId = id || generatedId
  const toggleLabel = isVisible ? t("hidePassword") : t("showPassword")

  return (
    <div className="position-relative">
      <Form.Control
        id={inputId}
        type={isVisible ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        className={`border-0 shadow-sm ${className}`}
        style={{
          borderRadius: "12px",
          // Extra padding on the right keeps long values clear of the toggle button
          padding: "12px 48px 12px 16px",
          fontSize: "16px",
          backgroundColor: "#f8fafc",
          border: "1px solid rgba(60, 130, 245, 0.2)",
          ...style,
        }}
      />
      <button
        type="button"
        className="password-toggle-btn position-absolute d-flex align-items-center justify-content-center"
        onClick={() => setIsVisible((visible) => !visible)}
        disabled={disabled}
        aria-label={toggleLabel}
        aria-controls={inputId}
        title={toggleLabel}
        style={{
          top: 0,
          right: 0,
          height: "100%",
          width: "48px",
          padding: 0,
          background: "transparent",
          border: "none",
          borderRadius: "0 12px 12px 0",
          cursor: "pointer",
        }}
      >
        {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  )
}
